/**
 * `<FilterChips>` — section 3 of the B-1 VM Debug pane.
 *
 * Renders one toggle chip per `VmMessageKind`. Selecting / deselecting
 * a chip filters the rendered stream. Filter state is owned by the
 * pane root (`index.tsx`) and threaded down — this component is
 * presentational.
 *
 * Default: all chips ON. Engineers can mute heartbeat / lifecycle
 * noise when triaging real traffic.
 */

import type { VmMessageKind } from '@volley/crucible-ipc-types/vm-message-stream';

export const ALL_FILTER_KINDS: VmMessageKind[] = [
  'request',
  'response',
  'stream-chunk',
  'stream-end',
  'audit-emit',
  'heartbeat',
  'lifecycle',
];

const KIND_LABEL: Record<VmMessageKind, string> = {
  request: 'Requests',
  response: 'Responses',
  'stream-chunk': 'Stream chunks',
  'stream-end': 'Stream ends',
  'audit-emit': 'Audit',
  heartbeat: 'Heartbeats',
  lifecycle: 'Lifecycle',
};

export interface FilterChipsProps {
  /** Set of currently-enabled kinds. Empty set = nothing rendered. */
  enabledKinds: Set<VmMessageKind>;
  /** Called when the user toggles a chip. Parent updates the set. */
  onToggle: (kind: VmMessageKind) => void;
}

export function FilterChips({
  enabledKinds,
  onToggle,
}: FilterChipsProps): React.JSX.Element {
  return (
    <div
      data-testid="vm-debug-filter-chips"
      className="sticky top-[40px] z-10 flex flex-wrap gap-1 border-b border-white/10 bg-black/40 px-3 py-1.5 text-[10px]"
    >
      {ALL_FILTER_KINDS.map((kind) => {
        const enabled = enabledKinds.has(kind);
        return (
          <button
            key={kind}
            type="button"
            data-testid={`vm-debug-filter-${kind}`}
            data-enabled={enabled}
            onClick={() => onToggle(kind)}
            className={`rounded-full px-2 py-0.5 transition-colors ${
              enabled
                ? 'bg-white/10 text-white/80'
                : 'bg-white/0 text-white/30 line-through'
            }`}
            aria-pressed={enabled}
          >
            {KIND_LABEL[kind]}
          </button>
        );
      })}
    </div>
  );
}
